/**
 * Окно «Добавить» видео-референс (спека §12): вкладки «Ссылка» и «Файл».
 * Успех — переход в карточку; `existing` (такой ролик уже был) — тот же переход с плашкой (state).
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Dialog, DialogFooter } from "@/components/trends/Dialog";
import { btn, btnPrimary, ErrorLine, input, label, Segmented } from "@/components/trends/ui";
import type { CreatedRef } from "@/lib/videoRef";
import { useCreateVideoRef, useUploadVideoRef } from "@/lib/videoRefApi";

type Tab = "link" | "file";

const ACCEPT = "video/mp4,video/quicktime";

export function AddRefDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const create = useCreateVideoRef();
  const upload = useUploadVideoRef();
  const [tab, setTab] = useState<Tab>("link");
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [drag, setDrag] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  const pending = create.isPending || upload.isPending;
  const error = tab === "link" ? create.error : upload.error;

  const done = (got: CreatedRef) => {
    onClose();
    navigate(`/video-refs/${got.ref.ref.id}`, { state: got.existing ? { existing: true } : undefined });
  };

  const pickFile = (f: File | undefined) => {
    upload.reset();
    if (f) setFile(f);
  };

  const submit = () => {
    if (pending) return;
    if (tab === "link") {
      if (url.trim()) create.mutate({ url: url.trim(), ...(note.trim() && { note: note.trim() }) }, { onSuccess: done });
    } else if (file) {
      upload.mutate({ file, ...(note.trim() && { note: note.trim() }) }, { onSuccess: done });
    }
  };

  const ready = tab === "link" ? url.trim() !== "" : file !== null;

  return (
    <Dialog open={open} title="Добавить видео-референс" onClose={onClose}>
      <div className="grid gap-3 px-4 py-3" data-annot="окно добавить видео-референс">
        <Segmented<Tab>
          label="Откуда"
          value={tab}
          onPick={setTab}
          options={[
            { value: "link", label: "Ссылка" },
            { value: "file", label: "Файл" },
          ]}
        />

        {tab === "link" ? (
          <label className="grid gap-1">
            <span className={label}>Адрес ролика</span>
            <input
              className={input}
              value={url}
              autoFocus
              placeholder="https://…"
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
            <span className="text-[11.5px] text-fg-faint">Instagram Reel, TikTok, Pinterest, YouTube Shorts</span>
          </label>
        ) : (
          <div className="grid gap-1">
            <span className={label}>Видеофайл</span>
            <div
              data-annot="файл видео-референса"
              onDragOver={(e) => {
                e.preventDefault();
                setDrag(true);
              }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDrag(false);
                pickFile(e.dataTransfer.files[0]);
              }}
              className={
                "flex flex-col items-center gap-2 rounded-[var(--radius-ui)] border border-dashed px-3 py-6 text-[12.5px] " +
                (drag ? "border-accent bg-accent/10" : "border-border")
              }
            >
              <span className="text-fg-muted">{file ? file.name : "Перетащите файл сюда"}</span>
              <button type="button" className={btn} onClick={() => picker.current?.click()}>
                Выбрать файл
              </button>
              <input
                ref={picker}
                type="file"
                accept={ACCEPT}
                hidden
                onChange={(e) => {
                  pickFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </div>
            <span className="text-[11.5px] text-fg-faint">MP4 или MOV, до 150 МБ, до 3 минут</span>
          </div>
        )}

        <label className="grid gap-1">
          <span className={label}>Комментарий (необязательно)</span>
          <input className={input} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        {pending && tab === "file" ? <div className="text-[12.5px] text-fg-muted">Загружаю файл…</div> : null}
        <ErrorLine error={error} />
      </div>
      <DialogFooter>
        <button type="button" className={btn} onClick={onClose}>
          Отмена
        </button>
        <button type="button" className={btnPrimary} disabled={!ready || pending} onClick={submit}>
          {pending ? "Добавляю…" : "Добавить"}
        </button>
      </DialogFooter>
    </Dialog>
  );
}
